import { ReferenceResponseFile } from "../components/order-components/DownloadGeneralReferenceDocs";
import {
  ReferenceDetail,
  ReferenceDetailContactInfo,
  SimpleReferenceDetail,
  SimpleReferenceDetailStatus,
} from "../interfaces/referenceDetaill";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertFileToBase64 } from "./helpers";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getReferenceDetailsByReferenceOrderID = async (
  referenceOrderID: number,
): Promise<ReferenceDetail[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const referenceDetailsResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/GetReferenceDetailsByReferenceOrderID/${referenceOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!referenceDetailsResponse.ok) {
    throw new Error("Failed to fetch reference details");
  }

  const referenceDetails = await referenceDetailsResponse.json();

  return referenceDetails;
};

export const getReferenceDetailByID = async (
  referenceDetailID: number,
): Promise<ReferenceDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const referenceDetailResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/GetReferenceDetailByID/${referenceDetailID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!referenceDetailResponse.ok) {
    throw new Error("Failed to fetch reference detail");
  }

  const referenceDetail = await referenceDetailResponse.json();

  return referenceDetail;
};

export const getSimpleReferenceDetailByID = async (
  referenceDetailID: number,
): Promise<SimpleReferenceDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceDetail/GetSimpleReferenceDetailByID/${referenceDetailID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch reference detail");
  }

  const simpleReferenceDetail: SimpleReferenceDetail = await response.json();

  return simpleReferenceDetail;
};

export const createReferenceDetail = async (
  referenceDetailCreate: ReferenceDetail,
): Promise<ReferenceDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/CreateReferenceDetail`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(referenceDetailCreate),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to create reference detail.");
  }
  return response.json();
};

export const updateReferenceDetail = async (
  referenceDetail: ReferenceDetail,
): Promise<ReferenceDetail> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/UpdateReferenceDetail`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(referenceDetail),
    },
  );

  if (!response.ok) {
    throw new Error("Failed to save reference detail.");
  }
  return response.json();
};

export const uploadReceivedForm = async (
  file: File,
  referenceDetailID: number,
) => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${apiBaseUrl}/api/ReferenceDetail/UploadReceivedForm?referenceDetailID=${referenceDetailID}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to upload received form.");
  }

  return response.json();
};

export const deleteReferenceDetail = async (
  referenceDetailID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/DeleteReferenceDetail/${referenceDetailID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to delete reference detail with ID ${referenceDetailID}.`,
    );
  }
};

export const getReferencesByCandidateID = async (
  candidateID: number,
): Promise<SimpleReferenceDetailStatus[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const referenceDetailsResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/GetReferencesByCandidateID/${candidateID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!referenceDetailsResponse.ok) {
    throw new Error("Failed to fetch reference details");
  }

  const referenceDetails = await referenceDetailsResponse.json();

  return referenceDetails;
};

export const uploadGeneralReferenceDocument = async (
  referenceDetail: ReferenceDetail,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);
    const formData = {
      referenceDetail,
      fileBase64: base64Data,
      contentType,
      fileName: file.name,
    };
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceDetail/UploadGeneralReferenceDocument`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );
    if (!response.ok) {
      console.error("Failed to upload reference document.");
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};
export const GetAllGeneralReferenceDocuments = async (
  referenceDetailID: number,
): Promise<ReferenceResponseFile[]> => {
  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }
  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/GetAllGeneralReferenceDocuments?referenceDetailID=${referenceDetailID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error("Failed to fetch reference document files");
  }
  const files: ReferenceResponseFile[] = await response.json();
  return files;
};

export const getDataForIncompleteTable = async (): Promise<
  ReferenceDetailContactInfo[] | null
> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceDetail/GetDataForIncompleteTable`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );

    if (!response.ok) {
      throw new Error("Failed to fetch incomplete table data");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching incomplete table data:", error);
    return null;
  }
};

export const deleteReferenceDetailResponse = async (
  referenceDetailID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const formData = {
    referenceDetailID,
  };

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/DeleteReferenceDetailResponse`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to delete reference detail response with ID ${referenceDetailID}.`,
    );
  }
};

export const downloadReferenceResponses = async (
  referenceDetailIDs: number[],
) => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const body = {
    referenceDetailIDs,
  };

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceDetail/DownloadReferenceResponses`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  return response.blob();
};
